// Shopify native Recommendations provider (scenario 3 only).
//
// Shopify's endpoint accepts a single product id, so we anchor the request on
// the highest-value paying item in the cart and ask for complementary products.

import { payingItems } from "./bundles";
import type {
  Cart,
  RecommendationProvider,
  UpsellConfig,
  UpsellRecommendation,
} from "./types";

interface ShopifyRecVariant {
  id: number;
  available: boolean;
  price: number;
  compare_at_price: number | null;
  title: string;
}

interface ShopifyRecProduct {
  id: number;
  title: string;
  featured_image: string | null;
  price: number;
  compare_at_price: number | null;
  variants: ShopifyRecVariant[];
}

function toRecommendation(product: ShopifyRecProduct): UpsellRecommendation | null {
  const variant =
    product.variants.find((v) => v.available) ?? product.variants[0];
  if (!variant) return null;

  return {
    id: variant.id,
    productTitle: product.title,
    variantTitle: variant.title !== "Default Title" ? variant.title : undefined,
    image: product.featured_image ?? "",
    price: variant.price,
    compareAtPrice:
      variant.compare_at_price && variant.compare_at_price > variant.price
        ? variant.compare_at_price
        : undefined,
    actionType: "add",
    actionLabel: "Add to Cart",
  };
}

export const shopifyProvider: RecommendationProvider = {
  async getComplementary(
    cart: Cart,
    limit: number,
    config: UpsellConfig,
  ): Promise<UpsellRecommendation[]> {
    const items = payingItems(cart.items ?? [], config.giftVariantId);
    if (items.length === 0) return [];

    // Anchor on the most expensive paying product.
    const anchor = items.reduce((max, item) =>
      item.price > max.price ? item : max,
    );

    const inCart = new Set(items.map((item) => item.product_id));
    const intent = config.shopify.intent || "related";

    const params = new URLSearchParams({
      product_id: String(anchor.product_id),
      intent,
      limit: String(limit),
    });

    const response = await fetch(`/recommendations/products.json?${params}`);
    if (!response.ok) return [];

    const { products = [] } = (await response.json()) as {
      products?: ShopifyRecProduct[];
    };

    return products
      .filter((product) => !inCart.has(product.id))
      .map(toRecommendation)
      .filter((rec): rec is UpsellRecommendation => rec !== null)
      .slice(0, limit);
  },
};
